import type { RouteLocationNormalized } from 'vue-router'
import { ROUTES } from '@/router/routeNames'
import {
  FunnelIcon,
  PuzzlePieceIcon,
  WindowIcon,
  ChatBubbleBottomCenterIcon,
  UserCircleIcon,
  GiftIcon,
  InboxIcon,
  AtSymbolIcon,
  RocketLaunchIcon,
  WrenchScrewdriverIcon,
  PresentationChartLineIcon,
} from '@heroicons/vue/24/outline'

export interface RecentPage {
  routeName: string
  params: Record<string, string>
  label: string
  typeLabel: string
  timestamp: number
}

/** Look up the icon for a recent page entry from TRACKED_ROUTES config */
export function getRecentPageIcon(routeName: string) {
  return TRACKED_ROUTES[routeName]?.icon
}

/**
 * Configuration: which routes to track and how to resolve their labels.
 *
 * Detail routes (with paramKey + resolve):
 *   Track individual resource pages (e.g., a specific flow or widget).
 *
 * Static routes (with label, no paramKey):
 *   Track fixed pages like Growth Ideas or A/B Testing.
 */
const TRACKED_ROUTES: Record<
  string,
  {
    paramKey?: string
    typeLabel: string
    label?: string
    icon: any
    resolve?: (id: string) => string | undefined
  }
> = {
  [ROUTES.OFFERFUNNEL]: {
    paramKey: 'offerFunnelId',
    typeLabel: 'Flow',
    icon: FunnelIcon,
    resolve: (id) => useFlowStore().getById(id)?.name,
  },
  [ROUTES.WIDGET]: {
    paramKey: 'widgetId',
    typeLabel: 'Widget',
    icon: PuzzlePieceIcon,
    resolve: (id) => useWidgetStore().getById(id)?.name,
  },
  [ROUTES.CAMPAIGN]: {
    paramKey: 'campaignId',
    typeLabel: 'Campaign',
    icon: WindowIcon,
    resolve: (id) => useCampaignStore().getById(id)?.name,
  },
  [ROUTES.QUESTION]: {
    paramKey: 'segmentGroupId',
    typeLabel: 'Question',
    icon: ChatBubbleBottomCenterIcon,
    resolve: (id) => {
      const sg = useSegmentGroupStore().getById(id)
      return sg?.name
    },
  },
  [ROUTES.SEGMENTGROUP]: {
    paramKey: 'segmentGroupId',
    typeLabel: 'Segment Group',
    icon: UserCircleIcon,
    resolve: (id) => useSegmentGroupStore().getById(id)?.name,
  },
  [ROUTES.OFFER]: {
    paramKey: 'offerId',
    typeLabel: 'Offer',
    icon: GiftIcon,
    resolve: (id) => useOfferStore().getById(id)?.name,
  },
  [ROUTES.FORM]: {
    paramKey: 'offerId',
    typeLabel: 'Form',
    icon: InboxIcon,
    resolve: (id) => useOfferStore().getById(id)?.name,
  },
  [ROUTES.EMAIL]: {
    paramKey: 'emailId',
    typeLabel: 'Email',
    icon: AtSymbolIcon,
    resolve: (id) => usePersonalizedEmailStore().getById(id)?.name,
  },
  [ROUTES.GROWTH_IDEAS]: {
    typeLabel: 'Optimize',
    label: 'Growth Ideas',
    icon: RocketLaunchIcon,
  },
  [ROUTES.OPTIMIZATIONS]: {
    typeLabel: 'Optimize',
    label: 'Optimizations',
    icon: WrenchScrewdriverIcon,
  },
  [ROUTES.SPLIT_TESTS]: {
    typeLabel: 'Optimize',
    label: 'A/B Testing',
    icon: PresentationChartLineIcon,
  },
}

const MAX_ENTRIES = 5

function getStorageKey(teamPid: string): string {
  return `rm_recent_pages_${teamPid}`
}

function loadFromStorage(teamPid: string): RecentPage[] {
  try {
    const raw = localStorage.getItem(getStorageKey(teamPid))
    return raw ? JSON.parse(raw) : []
  } catch {
    return []
  }
}

function saveToStorage(teamPid: string, pages: RecentPage[]): void {
  localStorage.setItem(getStorageKey(teamPid), JSON.stringify(pages))
}

const recentPages = ref<RecentPage[]>([])
const currentPage = ref<RecentPage | null>(null)
let activeTeamPid = ''

export function useRecentPages() {
  /**
   * Initialize with the current team's stored history.
   * Call this when team context becomes available.
   */
  function init(teamPid: string) {
    if (teamPid === activeTeamPid) return
    activeTeamPid = teamPid
    recentPages.value = loadFromStorage(teamPid)
    currentPage.value = recentPages.value[0] || null
  }

  /**
   * Called from router.afterEach to record a page visit.
   */
  function recordVisit(to: RouteLocationNormalized) {
    const teamPid = to.params.teamPid as string
    if (teamPid) init(teamPid)

    const routeName = to.name as string
    const config = TRACKED_ROUTES[routeName]
    if (!config) {
      currentPage.value = null
      return
    }

    if (!teamPid) return

    let label: string
    let params: Record<string, string> = { teamPid }

    if (config.paramKey) {
      // Detail route: resolve label from store
      const resourceId = to.params[config.paramKey] as string
      if (!resourceId) return

      const name = config.resolve?.(resourceId)
      if (!name) return

      label = name
      params[config.paramKey] = resourceId
    } else {
      // Static route: use fixed label
      label = config.label!
    }

    const entry: RecentPage = {
      routeName,
      params,
      label,
      typeLabel: config.typeLabel,
      timestamp: Date.now(),
    }

    // Set as current page
    currentPage.value = entry

    // Remove any existing entry for the same route+resource
    const filtered = recentPages.value.filter(
      (p) => {
        if (p.routeName !== routeName) return true
        // For detail routes, match by resource ID; for static routes, match by route name alone
        if (config.paramKey) return p.params[config.paramKey] !== params[config.paramKey]
        return false
      }
    )

    // Add to front, cap at MAX_ENTRIES
    recentPages.value = [entry, ...filtered].slice(0, MAX_ENTRIES)
    saveToStorage(teamPid, recentPages.value)
  }

  return {
    recentPages: readonly(recentPages),
    currentPage: readonly(currentPage),
    init,
    recordVisit,
  }
}
